import React, { Component } from "react";
import styled from "styled-components";
import bresenham from "bresenham/generator";
import {
  FormGroup,
  Button,
  ButtonGroup,
  Divider,
  InputGroup,
  Switch,
  Alert
} from "@blueprintjs/core";
import { ChromePicker } from "react-color";
import Led from "./Led";
import shallowCompare from "react-addons-shallow-compare";
import Measure from "react-measure";
import histogram from "../../histogram";
import { Flex } from "reflexbox";

const EditorContainer = styled.div`
  flex: 1;
  display: flex;
  user-select: none;
`;

const Palette = styled.div`
  padding: 0.5rem;
  padding-right: 0;
  padding-bottom: 0;
  display: flex;
  flex-wrap: wrap;
  overflow: scroll;
  max-height: 100px;
  box-shadow: 0 0 0 1px rgba(15, 15, 16, 0.6),
    inset 0 1px 2px rgba(15, 15, 16, 0.2);
  background-color: #28282a;
`;

const ColorWrapper = styled.div`
  display: block;
  width: calc(100% / 6);
  padding-right: 0.5em;
  padding-bottom: 0.5em;
`;

const ColorBox = styled.div`
  cursor: pointer;
  width: 100%;
  padding-top: 100%;
  border-radius: 4px;
  box-shadow: 0 0 0 1px rgba(16, 22, 26, 0.6),
    inset 0 1px 2px rgba(16, 22, 26, 0.2);
`;

const Histogram = styled.div`
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(16, 22, 26, 0.6),
    inset 0 1px 2px rgba(16, 22, 26, 0.2);
  background-color: hsl(240, 3%, 12%);
  height: 100px;
  margin-bottom: 1rem;
`;

const Canvas = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background: hsl(240, 3%, 12%);
`;

const Panel = styled.div`
  max-width: 960px;
  max-height: 960px;
  display: flex;
  flex-wrap: wrap;
`;

const Toolbar = styled.div`
  background: hsl(240, 2%, 21%);
  border-left: 1px solid hsl(240, 2%, 29%);
  flex: 0 0 calc(256px + 2rem);
  padding: 1rem;
  overflow-y: auto;
  overflow-x: hidden;
`;

function getRowCol(index) {
  const row = Math.floor(index / 32);
  const column = index % 32;
  return { row, column };
}

interface Props {
  document?: {
    title: string;
    data: string;
    user: string;
  };
  live?: boolean;
  handleSave: (data: string, title: string) => void;
  pushToLive?: (data: string, type: string) => void;
  handleDelete?: () => void;
}

class Editor extends Component<Props> {
  state = {
    title: "",
    pixels: null,
    tempPixels: [],
    tool: "point",
    points: [],
    color: "#" + Math.floor(Math.random() * 16777215).toString(16),
    undoSteps: [],
    redoSteps: [],
    mouseDown: false,
    showBorders: true,
    deleteConfirm: false,
    dimensions: {
      width: 0,
      height: 0
    },
    histogram: {
      blue: [],
      green: [],
      red: [],
      palettes: { rgb: [] },
      max: 1024
    }
  };

  shouldComponentUpdate(nextProps, nextState) {
    if (
      this.props.document &&
      this.props.document.data &&
      nextProps.document.data !== this.props.document.data
    ) {
      this.updatePixels(nextProps.document.data);
    }
    return shallowCompare(this, nextProps, nextState);
  }

  componentDidMount() {
    if (this.props.document !== null) {
      this.updatePixels(this.props.document.data);
      this.setState({ title: this.props.document.title });
    } else {
      this.updatePixels("0".repeat(6 * 32 * 32));
    }
  }

  updatePixels = colorData => {
    let pixels = [];
    const rows = colorData.match(/.{1,192}/g);
    for (const row in rows) {
      const columns = rows[row].match(/.{1,6}/g);
      for (const column in columns) {
        pixels.push("#" + columns[column]);
      }
    }
    this.setState({ pixels });
    histogram(pixels, (err, data) => {
      this.setState({ histogram: data });
    });
  };

  saveDesign = pixels => {
    let data = "";
    for (let color of pixels) {
      data += color.replace(/#/g, "");
    }
    this.props.handleSave(data, this.state.title);
  };

  delete = () => {
    this.setState({ deleteConfirm: false });
    this.props.handleDelete();
  };

  pushToLive = () => {
    let data = "";
    for (let color of this.state.pixels) {
      data += color.replace(/#/g, "");
    }
    this.props.pushToLive(data, "art");
  };

  setColor = color => {
    this.setState({ color: color.hex });
  };

  setPixels = pixels => {
    let undoSteps = [...this.state.undoSteps];
    // Add current pixels to history
    undoSteps.push(this.state.pixels);
    // Set new values and clear redo
    this.setState({ undoSteps, pixels, redoSteps: [] });
    histogram(pixels, (err, data) => {
      this.setState({ histogram: data });
    });
    if (this.props.live === true) {
      this.saveDesign(pixels);
    }
  };

  handleUndo = () => {
    let undoSteps = [...this.state.undoSteps];
    let redoSteps = [...this.state.redoSteps];
    const pixels = undoSteps.pop();
    redoSteps.push(this.state.pixels);
    this.setState({ undoSteps, redoSteps, pixels });
  };

  handleRedo = () => {
    let undoSteps = [...this.state.undoSteps];
    let redoSteps = [...this.state.redoSteps];
    const pixels = redoSteps.pop();
    undoSteps.push(this.state.pixels);
    this.setState({ undoSteps, redoSteps, pixels });
  };

  handleClick = index => {
    switch (this.state.tool) {
      case "point":
        this.handlePointClick(index);
        break;
      case "line":
        this.handleLineClick(index);
        break;
      case "rect":
        this.handleRectangleClick(index);
        break;
      default:
        return;
    }
  };

  handleMouseEnter = index => {
    switch (this.state.tool) {
      case "rect":
        this.handleRectangleMouseEnter(index);
        break;
      case "brush":
        this.handleBrushMouseEnter(index);
        break;
      case "line":
        this.handleLineMouseEnter(index);
        break;
      default:
        return;
    }
  };

  handleMouseDown = index => {
    this.setState({ mouseDown: true });
    switch (this.state.tool) {
      case "brush":
        this.handleBrushMouseDown(index);
        break;
      default:
        return;
    }
  };

  handleMouseUp = index => {
    this.setState({ mouseDown: false });
  };

  handlePointClick = index => {
    let pixels = [...this.state.pixels];
    pixels[index] = this.state.color;
    this.setPixels(pixels);
  };

  handleBrushMouseDown = index => {
    let pixels = [...this.state.pixels];
    pixels[index] = this.state.color;
    // Set pixels again so they get added to undo history
    this.setPixels(pixels);
  };

  handleBrushMouseEnter = index => {
    if (this.state.mouseDown === false) return;
    let pixels = [...this.state.pixels];
    pixels[index] = this.state.color;
    // Don't use this.setPixels so indiviual pixels won't be added to
    // undo history
    this.setState({ pixels });
  };

  handleLineClick = index => {
    // Clicked first pixel
    if (this.state.points.length === 0) {
      let pixels = [...this.state.tempPixels];
      pixels[index] = this.state.color;
      this.setState({ tempPixels: pixels, points: [index] });
      // Clicked second pixel
    } else {
      // Merge temp pixels with actual pixels
      const pixels = this.state.pixels.map((color, i) =>
        this.state.tempPixels[i] !== undefined
          ? this.state.tempPixels[i]
          : color
      );
      // Add these pixels to the screen
      this.setPixels(pixels);
      // Reset tool and temp pixels
      this.setState({ points: [], tempPixels: [] });
    }
  };

  handleLineMouseEnter = index => {
    if (this.state.points.length === 1) {
      const { row: startRow, column: startCol } = getRowCol(
        this.state.points[0]
      );
      const { row: endRow, column: endCol } = getRowCol(index);

      let pixels = [];
      for (const point of bresenham(startRow, startCol, endRow, endCol)) {
        pixels[point.x * 32 + point.y] = this.state.color;
      }

      this.setState({ tempPixels: pixels });
    }
  };

  handleRectangleClick = index => {
    // Clicked first pixel
    if (this.state.points.length === 0) {
      let pixels = [...this.state.tempPixels];
      pixels[index] = this.state.color;
      this.setState({ tempPixels: pixels, points: [index] });
      // Clicked second pixel
    } else {
      // Merge temp pixels with actual pixels
      const pixels = this.state.pixels.map((color, i) =>
        this.state.tempPixels[i] !== undefined
          ? this.state.tempPixels[i]
          : color
      );
      // Add these pixels to the screen
      this.setPixels(pixels);
      // Reset tool and temp pixels
      this.setState({ points: [], tempPixels: [] });
    }
  };

  handleRectangleMouseEnter = index => {
    if (this.state.points.length === 1) {
      const { row: startRow, column: startCol } = getRowCol(
        this.state.points[0]
      );
      const { row: endRow, column: endCol } = getRowCol(index);
      // Height of rectangle
      const rows = Math.max(startRow, endRow) - Math.min(startRow, endRow);
      // Width of rectangle
      const columns = Math.max(startCol, endCol) - Math.min(startCol, endCol);

      let pixels = [];
      for (let i = 0; i <= rows; i++) {
        for (let i2 = 0; i2 <= columns; i2++) {
          pixels[
            (Math.min(startRow, endRow) + i) * 32 +
              Math.min(startCol, endCol) +
              i2
          ] = this.state.color;
        }
      }

      this.setState({ tempPixels: pixels });
    }
  };

  renderHistogram = () => {
    return (
      <Histogram>
        <svg
          width="256"
          height="100"
          viewBox="0 0 256 100"
          style={{ isolation: "isolate" }}
        >
          {this.state.histogram.red.map(
            (v, i) =>
              v > 0 && (
                <rect
                  key={`r-${i}`}
                  style={{ mixBlendMode: "screen" }}
                  x={i}
                  y={100 - (v / this.state.histogram.max) * 100}
                  width="1"
                  height={(v / this.state.histogram.max) * 100}
                  fill="#FF0000"
                />
              )
          )}
          {this.state.histogram.green.map(
            (v, i) =>
              v > 0 && (
                <rect
                  key={`g-${i}`}
                  style={{ mixBlendMode: "screen" }}
                  x={i}
                  y={100 - (v / this.state.histogram.max) * 100}
                  width="1"
                  height={(v / this.state.histogram.max) * 100}
                  fill="#00FF00"
                />
              )
          )}
          {this.state.histogram.blue.map(
            (v, i) =>
              v > 0 && (
                <rect
                  key={`b-${i}`}
                  style={{ mixBlendMode: "screen" }}
                  x={i}
                  y={100 - (v / this.state.histogram.max) * 100}
                  width="1"
                  height={(v / this.state.histogram.max) * 100}
                  fill="#0000FF"
                />
              )
          )}
        </svg>
      </Histogram>
    );
  };

  render() {
    const panelSize =
      Math.min(this.state.dimensions.width, this.state.dimensions.height) - 40;
    return (
      <EditorContainer
        onMouseLeave={this.handleMouseUp}
        onMouseUp={this.handleMouseUp}
      >
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          icon="trash"
          intent="danger"
          isOpen={this.state.deleteConfirm}
          onCancel={() => this.setState({ deleteConfirm: false })}
          onConfirm={this.delete}
          transitionDuration={1}
        >
          <p>
            Are you sure you want to delete <b>{this.state.title}</b>? Your
            design will permanently removed.
          </p>
        </Alert>
        <Measure
          bounds
          onResize={contentRect => {
            this.setState({ dimensions: contentRect.bounds });
          }}
        >
          {({ measureRef }) => (
            <Canvas ref={measureRef}>
              <Panel
                style={{
                  width: panelSize,
                  height: panelSize
                }}
              >
                {this.state.pixels !== null &&
                  this.state.pixels.map((color, index) => (
                    <Led
                      key={index}
                      color={
                        this.state.tempPixels[index] === undefined
                          ? color
                          : this.state.tempPixels[index]
                      }
                      index={index}
                      showBorders={this.state.showBorders}
                      handleClick={this.handleClick}
                      handleMouseEnter={this.handleMouseEnter}
                      handleMouseDown={this.handleMouseDown}
                      handleMouseUp={this.handleMouseUp}
                    />
                  ))}
              </Panel>
            </Canvas>
          )}
        </Measure>
        <Toolbar>
          {this.props.live !== true && (
            <React.Fragment>
              <FormGroup label="Title">
                <InputGroup
                  value={this.state.title}
                  onChange={e => this.setState({ title: e.target.value })}
                />
              </FormGroup>
              <Flex flexDirection="column">
                <Button
                  intent="success"
                  large
                  onClick={() => this.saveDesign(this.state.pixels)}
                  disabled={this.state.title === ""}
                >
                  Save design
                </Button>
                <br />
                <Button onClick={this.pushToLive}>Push to live</Button>
              </Flex>
              <br />
              <Divider />
              <br />
              <ButtonGroup fill>
                <Button
                  disabled={this.state.undoSteps.length === 0}
                  onClick={this.handleUndo}
                >
                  Undo
                </Button>
                <Button
                  disabled={this.state.redoSteps.length === 0}
                  onClick={this.handleRedo}
                >
                  Redo
                </Button>
              </ButtonGroup>
              <br />
              <Divider />
              <br />
            </React.Fragment>
          )}
          <FormGroup label="Tools">
            <ButtonGroup
              fill
              alignText="left"
              style={{ marginBottom: "0.5rem" }}
            >
              <Button
                icon="edit"
                active={this.state.tool === "point"}
                onClick={() => this.setState({ tool: "point" })}
                disabled={this.state.points.length > 0}
              >
                Point
              </Button>
            </ButtonGroup>
            <ButtonGroup
              fill
              alignText="left"
              style={{ marginBottom: "0.5rem" }}
            >
              <Button
                icon="style"
                active={this.state.tool === "brush"}
                onClick={() => this.setState({ tool: "brush" })}
                disabled={this.state.points.length > 0}
              >
                Brush
              </Button>
            </ButtonGroup>
            <ButtonGroup
              fill
              alignText="left"
              style={{ marginBottom: "0.5rem" }}
            >
              <Button
                icon="minus"
                active={this.state.tool === "line"}
                onClick={() => this.setState({ tool: "line" })}
                disabled={this.state.points.length > 0}
              >
                Line
              </Button>
            </ButtonGroup>
            <ButtonGroup
              fill
              alignText="left"
              style={{ marginBottom: "0.5rem" }}
            >
              <Button
                icon="symbol-square"
                active={this.state.tool === "rect"}
                onClick={() => this.setState({ tool: "rect" })}
                disabled={this.state.points.length > 0}
              >
                Rectangle
              </Button>
            </ButtonGroup>
          </FormGroup>
          <FormGroup label="Color">
            <ChromePicker
              width={256}
              disableAlpha
              color={this.state.color}
              onChangeComplete={this.setColor}
            />
          </FormGroup>
          <FormGroup label="Palette">
            <Palette>
              {this.state.histogram.palettes.rgb.map(color => (
                <ColorWrapper key={color}>
                  <ColorBox
                    style={{ backgroundColor: color }}
                    onClick={() => this.setState({ color })}
                  />
                </ColorWrapper>
              ))}
            </Palette>
          </FormGroup>
          <FormGroup label="View options">
            <Switch
              checked={this.state.showBorders}
              label="Show pixel borders"
              onChange={() =>
                this.setState({ showBorders: !this.state.showBorders })
              }
            />
          </FormGroup>
          <FormGroup label="Histogram">{this.renderHistogram()}</FormGroup>

          {this.props.document && this.props.live !== true && (
            <>
              <Divider />
              <br />
              <Button
                intent="danger"
                onClick={() => this.setState({ deleteConfirm: true })}
              >
                Delete design
              </Button>
            </>
          )}
        </Toolbar>
      </EditorContainer>
    );
  }
}

export default Editor;
